import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Receipt, FileText, Wallet,
  Banknote, ArrowRightLeft, Lock,
  ChevronDown, ChevronUp, Zap, ClipboardList,
  Truck, CreditCard, UserCheck, BarChart3,
} from 'lucide-react';
import { api, useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Map QuickLaunch action keys to featureFlag IDs (null = always-on, never filtered)
const ACTION_FEATURE_MAP = {
  'sales':               null,              // core POS — always on
  'receive-payment':     null,              // core — always on
  'purchase-order':      'purchase_orders',
  'pay-supplier':        'pay_supplier',
  'expense':             null,              // always on
  'branch-transfer':     'branch_transfers',
  'inter-branch-pickup': 'branch_pickups',
  'inter-branch-payment':'cross_branch_payments',
  'stock-request':       'branch_transfers',
  'capital-changes':     null,              // report — gated by permission, not plan
  'close-shift':         'multi_cashier_shifts',
  'close-wizard':        null,              // always on
};

/**
 * QuickLaunch — collapsible grid of the most-used manager actions.
 * - Pinned at the top of the Dashboard for fast access
 * - Collapsed state persists per-user in localStorage
 * - Purely navigation; doesn't alter any business state
 *
 * Sort order rationale:
 *   Tier 1 — Core revenue:    Sales, Receive Payment
 *   Tier 2 — Procurement:     Purchase Order, Pay Supplier, Expense
 *   Tier 3 — Stock movement:  Stock Request → Branch Transfer (the pair the
 *                             owner works as one flow, 2026-09-04), then
 *                             Inter-Branch Pickup
 *   Tier 4 — Inter-branch $:  Inter-Branch Payment
 *   Tier 5 — Reports:         Capital Changes (owner: one click to the report)
 *   Tier 6 — End of day:      Close Shift, Closing Wizard
 */
const ACTIONS = [
  // ── Tier 1: Core revenue ────────────────────────────────────────────────
  {
    key: 'sales',
    label: 'Sales',
    icon: ShoppingCart,
    path: '/sales-new',
    color: '',
    iconColor: '#FFFFFF',
    iconBg: '#1A4D2E',   // Forest — brand primary
  },
  {
    key: 'receive-payment',
    label: 'Receive Payment',
    icon: Receipt,
    path: '/payments',
    color: '',
    iconColor: '#FFFFFF',
    iconBg: '#1E3A8A',   // Blue-900 — AR
  },
  // ── Tier 2: Procurement ─────────────────────────────────────────────────
  {
    key: 'purchase-order',
    label: 'Purchase Order',
    icon: FileText,
    path: '/purchase-orders',
    color: '',
    iconColor: '#FFFFFF',
    iconBg: '#4C1D95',   // Violet-900 — procurement
  },
  {
    key: 'pay-supplier',
    label: 'Pay Supplier',
    icon: Wallet,
    path: '/pay-supplier',
    color: '',
    iconColor: '#FFFFFF',
    iconBg: '#1E40AF',   // Blue-800 — payables
  },
  {
    key: 'expense',
    label: 'Expense',
    icon: Banknote,
    path: '/accounting',
    color: '',
    iconColor: '#FFFFFF',
    iconBg: '#7F1D1D',   // Red-900 — outflows
  },
  // ── Tier 3: Stock movement — request first, then the transfer it becomes ─
  {
    key: 'stock-request',
    label: 'Stock Request',
    icon: ClipboardList,
    path: '/stock-requests',
    color: '',
    iconColor: '#FFFFFF',
    iconBg: '#134E4A',   // Teal-900 — requests
  },
  {
    key: 'branch-transfer',
    label: 'Branch Transfer',
    icon: ArrowRightLeft,
    path: '/branch-transfers',
    color: '',
    iconColor: '#FFFFFF',
    iconBg: '#78350F',   // Amber-900 — movement
  },
  {
    key: 'inter-branch-pickup',
    label: 'Inter-Branch Pickup',
    icon: Truck,
    path: '/branch-pickups',
    color: '',
    iconColor: '#FFFFFF',
    iconBg: '#0C4A6E',   // Sky-900 — logistics
  },
  // ── Tier 4: Inter-branch payments ───────────────────────────────────────
  {
    key: 'inter-branch-payment',
    label: 'Inter-Branch Payment',
    icon: CreditCard,
    path: '/cross-branch-payments',
    color: '',
    iconColor: '#FFFFFF',
    iconBg: '#7C2D12',   // Orange-900 — interbank
  },
  // ── Tier 5: Reports ─────────────────────────────────────────────────────
  {
    key: 'capital-changes',
    label: 'Capital Changes',
    icon: BarChart3,
    path: '/capital-change-report',
    color: '',
    iconColor: '#FFFFFF',
    iconBg: '#3F3F46',   // Zinc-700 — reporting
    perm: ['products', 'edit'],   // same gate as the sidebar entry
  },
  // ── Tier 6: End of day ──────────────────────────────────────────────────
  {
    key: 'close-shift',
    label: 'Close Shift',
    icon: UserCheck,
    path: '/shifts',
    color: '',
    iconColor: '#FFFFFF',
    iconBg: '#312E81',   // Indigo-900 — accountability
  },
  {
    key: 'close-wizard',
    label: 'Closing Wizard',
    icon: Lock,
    path: '/close-wizard',
    color: '',
    iconColor: '#FFFFFF',
    iconBg: '#0F172A',   // Slate-900 — EOD
  },
];

const STORAGE_KEY = 'agripos_quick_launch_collapsed';

export default function QuickLaunch() {
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; }
    catch { return false; }
  });
  const [shiftsEnabled, setShiftsEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/shifts/feature-status')
      .then(res => {
        if (!cancelled) setShiftsEnabled(!!res.data?.enabled);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleCloseShift = async () => {
    try {
      const res = await api.get('/shifts/feature-status');
      const shift = res.data?.active_shift;
      if (shift?.shift_id) {
        navigate(`/close-shift?shift_id=${shift.shift_id}`);
      } else if (res.data?.requires_open) {
        toast.info('No active shift. Open a shift first before closing.');
      } else {
        navigate('/shifts');
      }
    } catch {
      navigate('/shifts');
    }
  };

  // Filter actions by both shift-feature-status and global featureFlags
  const features = user?.subscription?.features || {};
  const visibleActions = ACTIONS.filter(a => {
    if (a.key === 'close-shift') return shiftsEnabled;
    if (a.perm && typeof hasPerm === 'function' && !hasPerm(...a.perm)) return false;
    const fKey = ACTION_FEATURE_MAP[a.key];
    if (!fKey) return true;  // always-on actions
    return features[fKey] !== false;  // hidden when explicitly disabled
  });

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); }
    catch { /* localStorage unavailable */ }
  };

  return (
    <div
      className="rounded-lg bg-white overflow-hidden"
      style={{ border: '1px solid var(--border-default)' }}
      data-testid="quick-launch"
    >
      {/* Header — clickable to toggle */}
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between px-3 py-2.5 border-b transition-colors hover:bg-[var(--surface-canvas)]"
        style={{ borderColor: 'var(--border-subtle)' }}
        data-testid="quick-launch-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-[#1A4D2E] flex items-center justify-center">
            <Zap size={13} className="text-white" />
          </div>
          <span className="text-[13px] font-semibold" style={{ fontFamily: 'Manrope', color: 'var(--text-primary)' }}>
            Quick Launch
          </span>
          <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>{visibleActions.length} actions</span>
        </div>
        {collapsed ? (
          <ChevronDown size={15} style={{ color: 'var(--text-tertiary)' }} />
        ) : (
          <ChevronUp size={15} style={{ color: 'var(--text-tertiary)' }} />
        )}
      </button>

      {/* Body */}
      {!collapsed && (
        <div className="p-3">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
            {visibleActions.map(action => {
              const Icon = action.icon;
              return (
                <button
                  key={action.key}
                  type="button"
                  onClick={() => {
                    if (action.key === 'close-shift') {
                      handleCloseShift();
                    } else {
                      navigate(action.path);
                    }
                  }}
                  className="group flex flex-col items-center justify-center gap-1.5 py-3 px-2 rounded-lg border transition-colors duration-75"
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderColor: 'var(--border-default)',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--surface-canvas)')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#FFFFFF')}
                  data-testid={`quick-launch-${action.key}`}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-white transition-opacity duration-75 group-hover:opacity-90"
                    style={{ backgroundColor: action.iconBg }}
                  >
                    <Icon size={17} />
                  </div>
                  <span className="text-[11px] font-medium leading-tight text-center" style={{ color: 'var(--text-secondary)' }}>
                    {action.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
